'use client';

import React, { useState, useEffect } from 'react';
import { Grid, Text, Box, Notification, Stack } from '@mantine/core';
import { useTranslations } from 'next-intl';
import SelectionCard from '../SelectionCard';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface DiseaseInformationStepProps {
  diseaseTypes: string[];
  onDiseaseTypeSelect: (value: string) => void;
}

const DiseaseInformationStep: React.FC<DiseaseInformationStepProps> = ({
  diseaseTypes,
  onDiseaseTypeSelect,
}) => {
  const t = useTranslations('diseaseInformation');
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

  const diseaseOptions = [
    {
      value: 'healthy no issues',
      label: t('noIssues.label'),
      description: t('noIssues.description'),
      image: '/images/noIssues.jpeg',
    },
    {
      value: 'type 1 diabeties',
      label: t('type1.label'),
      description: t('type1.description'),
      image: '/images/type1.jpeg',
    },
    {
      value: 'type2 diabeties',
      label: t('type2.label'),
      description: t('type2.description'),
      image: '/images/type2.jpeg',
    },
    {
      value: 'gestational',
      label: t('gestational.label'),
      description: t('gestational.description'),
      image: '/images/gestational.jpeg',
    },
    {
      value: 'pcos',
      label: t('pcos.label'),
      description: t('pcos.description'),
      image: '/images/pcos.jpeg',
    },
    {
      value: 'metabolic disorder',
      label: t('metabolic.label'),
      description: t('metabolic.description'),
      image: '/images/metabolic.jpeg',
    },
    {
      value: 'insulin resistance',
      label: t('insulinResistance.label'),
      description: t('insulinResistance.description'),
      image: '/images/iR.jpeg',
    },
  ];

  useEffect(() => {
    const showNextCard = (index: number) => {
      if (index < diseaseOptions.length) {
        setVisibleCards((prev) => [...prev, index]);
        setTimeout(() => showNextCard(index + 1), 200); // Adjust delay as needed
      }
    };

    showNextCard(0);

    return () => {
      // Clear any remaining timeouts
      let i = 0;
      while (i < diseaseOptions.length) {
        clearTimeout(i);
        i++;
      }
    };
  }, []);

  const handleSelect = (value: string) => {
    if (diseaseTypes.includes(value)) {
      onDiseaseTypeSelect(value);
    } else if (diseaseTypes.length < 3) {
      onDiseaseTypeSelect(value);
    } else {
      setNotificationVisible(true);
      setTimeout(() => setNotificationVisible(false), 3000);
    }
  };

  return (
    <Box p={isMobile ? 'xs' : 'md'}>
      <Stack gap={isMobile ? 'xs' : 'md'}>
        <Text size={isMobile ? 'md' : 'lg'} fw={500}>
          {t('selectYourHealthConditions')}
        </Text>
        {notificationVisible && (
          <Notification
            title={t('notification.title')}
            color="red"
            onClose={() => setNotificationVisible(false)}
          >
            {t('notification.message')}
          </Notification>
        )}
        <Grid gutter={isMobile ? 'xs' : 'md'}>
          {diseaseOptions.map((option, index) => (
            <Grid.Col
              key={option.value}
              span={isMobile ? 12 : 6}
              style={{
                opacity: visibleCards.includes(index) ? 1 : 0,
                transform: `translateY(${visibleCards.includes(index) ? '0' : '20px'})`,
                transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out',
                visibility: visibleCards.includes(index) ? 'visible' : 'hidden',
              }}
            >
              <SelectionCard
                value={option.value}
                label={option.label}
                description={option.description}
                image={option.image}
                onSelect={handleSelect}
                isSelected={diseaseTypes.includes(option.value)}
              />
            </Grid.Col>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
};

export default DiseaseInformationStep;
